// Import the functions you need from the SDKs you need
import { FirebaseError, initializeApp } from "firebase/app"
import {
    indexedDBLocalPersistence,
    createUserWithEmailAndPassword,
    sendPasswordResetEmail,
    onAuthStateChanged,
    signInWithEmailAndPassword,
    signOut as fbSignOut,
    initializeAuth,
    sendEmailVerification,
    reauthenticateWithCredential,
    EmailAuthProvider,
    getAuth,
} from "firebase/auth"

import type { User } from "firebase/auth"
import {
    doc,
    initializeFirestore,
    persistentLocalCache,
    persistentMultipleTabManager,
    setDoc,
} from "firebase/firestore"
import { derived, get, writable } from "svelte/store"
import type { Readable } from "svelte/store"
import { createNotice } from "@components/bottomPopup/Notice.module"
import type { Profile } from "@store/user"
import { getFunctions, httpsCallable } from "firebase/functions"
import { logWrite } from "@utils/log"
import { setUser } from "@sentry/browser"
import { Capacitor } from "@capacitor/core"
import { App } from "@capacitor/app"

// TODO: Add SDKs for Firebase products that you want to use
// https://firebase.google.com/docs/web/setup#available-libraries

// Your web app's Firebase configuration
const firebaseConfig = {
    apiKey: "AIzaSyAV9m8m32AeYOjSO6WCa8dK-B36AtH6b5c",
    authDomain: "acim-ce.firebaseapp.com",
    projectId: "acim-ce",
    storageBucket: "acim-ce.appspot.com",
    messagingSenderId: "125778103038",
    appId: "1:125778103038:web:9ce8d9010fc3dcf8fcb3af",
}

// Initialize Firebase
const app = initializeApp(firebaseConfig)
const auth = Capacitor.isNativePlatform()
    ? initializeAuth(app, {
          persistence: indexedDBLocalPersistence,
      })
    : getAuth()

export const db = initializeFirestore(app, {
    localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
})
const functions = getFunctions(app)

/**
 * Responsible for notifying the server when a user is verified
 * so that their email and name can be pushed to Keap server
 */
export const sendToKeap = httpsCallable(functions, "sendToKeap")

export const user = writable<null | User>(null)
// eslint-disable-next-line @typescript-eslint/ban-ts-comment

/**
 * This is an attempt at fixing the iOS error of getting a white screen
 * {@link https://github.com/ionic-team/capacitor/discussions/7097#discussioncomment-11695244}
 * I removed the forceRefresh. Technically if it is expired, it should refresh.
 */
App.addListener("resume", () => {
    const currentUser = get(user)
    if (!currentUser) return
    console.log("Refreshing token...")
    currentUser
        .getIdToken()
        .then((token) => console.log("Token refreshed: ", token))
        .catch(console.error)
})

user.subscribe((user) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    window.user = user
})
export const getUserProfileDocPath = () => {
    const currentUser = get(user)
    if (currentUser === null) throw new Error("Asked for profile path but no user set")
    return "users/" + currentUser.uid
}
export const getUserProfileDoc = () => doc(db, getUserProfileDocPath())
onAuthStateChanged(auth, (userInfo) => {
    userNumber++
    user.set(userInfo)

    // Set user for sentry
    if (userInfo === null) {
        setUser(null)
    } else {
        setUser({
            email: userInfo.email ?? undefined,
            id: userInfo.uid,
            verified: userInfo.emailVerified,
        })
    }
})

export async function resetMyPassword() {
    const email = get(user)?.email
    if (!email) throw new Error("User was not defined or did not have an email associated with it.")
    return resetPassword(email)
}

export async function resetPassword(email: string) {
    if (!email?.length) throw new Error("Please enter an e-mail address")
    if (!email.includes("@")) throw new Error("Please enter a proper e-mail address")
    return sendPasswordResetEmail(auth, email)
}

export async function newUser(
    email: string,
    password: string,
    name: string,
    newsletter: boolean = false,
) {
    try {
        await createUserWithEmailAndPassword(auth, email, password)
        // const user = userCredential.user
        // return userCredential
        await registerNameAndNewsletter(name, newsletter)
        verifyEmail()
        createNotice({
            headline: "Thank you!",
            detail: "An e-mail has been dispatched to you, please confirm your address and return here to access all the features.",
        })
    } catch (error) {
        console.error({ error })
        throw error
    }
}

export async function registerNameAndNewsletter(name: string, newsletter: boolean) {
    if (get(userStatus) == "unverified" || get(userStatus) == "verified") {
        const [firstname, lastname] = name.split(" ", 2)
        const profile: Profile = {
            firstname,
            lastname,
            keap: newsletter ? "newsletter" : "regular",
        }

        const doc = getUserProfileDoc()
        logWrite(doc.path, profile)
        const setter = await setDoc(doc, profile, { merge: true })
        return setter
    } else {
        console.error("Not logged in! trying to set name")
    }
}

export async function verifyEmail() {
    const currentUser = get(user)
    if (currentUser !== null && !currentUser.emailVerified) {
        await sendEmailVerification(currentUser)
    } else {
        throw "Verify email was called but user was not logged in"
    }
}

export async function signIn(email: string, password: string) {
    const userCredential = await signInWithEmailAndPassword(auth, email, password)
    history.pushState("", "", "/")
    return userCredential
}

export async function signOut() {
    const rtn = await fbSignOut(auth)
    history.pushState("", "", "/")
    return rtn
}

export async function reauth(pwd: string) {
    const userData = get(user)
    if (!userData) throw new Error("User not logged in!")
    if (!userData.email) throw new Error("No e-mail for current user!")
    const credentials = EmailAuthProvider.credential(userData.email, pwd)
    return reauthenticateWithCredential(userData, credentials)
}

export function doesNeedCredentials(err: any) {
    return err instanceof FirebaseError && err.code === "auth/requires-recent-login"
}

/**
 * Attempts to delete the current account.
 * Expects `delete mypassword` as the string. Checks to see if
 * `mypassword` matches the user's password using reauthenticate.
 * Once re-authenticated, calls the firebase function deleteAccount
 * @param deleteString `delete [mypassword]` where [mypassword] is the users password - fails otherwise
 * @returns boolean true if pass, throws error if fail. It's a good idea to `errorize` error.
 */
export async function deleteAccount(deleteString: string): Promise<boolean> {
    const currentUser = get(user)
    if (!currentUser) throw new Error("Not logged in.")

    const del = deleteString.slice(0, 7)
    if (del != "delete ") throw new Error("`Delete ` was not written before password.")

    const pwd = deleteString.slice(7)
    const email = currentUser.email
    if (email === null)
        throw new Error(
            "Email was null in current user - this should never happen unless someone registered without an email",
        )
    const credentials = EmailAuthProvider.credential(email, pwd)

    await reauthenticateWithCredential(currentUser, credentials)
    await httpsCallable(functions, "deleteAccount")()
    await signOut()
    return true
}

export type UserStatus = "out" | "verified" | "unverified" | "undetermined"
export const userStatus: Readable<UserStatus> = derived(
    user,
    (user) => {
        if (user === undefined) return "undetermined" as const
        if (!user) return "out" as const
        if (!user.emailVerified) return "unverified" as const
        return "verified" as const
    },
    "undetermined",
)

userStatus.subscribe(() => console.log("👤 Changed user status: ", get(userStatus)))

//
// If we're doing ASYNC stuff and we want to make sure they don't leak into other users
// we can call a security token, which returns a function that returns true if we're on the
// same user state as its initial calling

let userNumber = 0
export const userSecurityToken = () => {
    const currentUserNumber = userNumber
    return () => userNumber === currentUserNumber
}

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
//@ts-ignore
window.newUser = newUser
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
//@ts-ignore
window.signIn = signIn
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
//@ts-ignore
window.signOut = signOut
